import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Check, RefreshCw, ChevronDown, ChevronUp, Wand2, Image, FileText, X, Upload, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { parseDocument } from '@/lib/documentParser';
import { toast } from 'sonner';

export interface AIGenerateContext {
  label: string;
  value: string;
  key: string;
}

interface UploadedImage {
  name: string;
  base64: string;
  preview: string;
}

interface UploadedDocument {
  name: string;
  text: string;
  wordCount: number;
}

export interface AIGenerateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  fieldName: string;
  context: AIGenerateContext[];
  onGenerate: (customPrompt?: string, attachments?: { images?: { name: string; base64: string }[]; documents?: { name: string; text: string }[] }) => Promise<string[]>;
  onApply: (value: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  multiline?: boolean;
  hideCustomize?: boolean;
  hideContextBadges?: boolean;
  allowAttachments?: boolean;
}

// Context keys that should be hidden from users but still passed to AI
const HIDDEN_CONTEXT_KEYS = ['agentType', 'difficulty', 'type'];

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_DOCUMENT_SIZE = 25 * 1024 * 1024; // 25MB
const MAX_IMAGES = 3;
const MAX_DOCUMENTS = 2;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const ALLOWED_DOCUMENT_TYPES = ['.pdf', '.docx', '.txt'];

export function AIGenerateModal({
  open,
  onOpenChange,
  title,
  fieldName,
  context,
  onGenerate,
  onApply,
  isLoading = false,
  placeholder = 'Click "Generate" to create content, or customize the prompt below...',
  multiline = false,
  hideCustomize = false,
  hideContextBadges = true,
  allowAttachments = true,
}: AIGenerateModalProps) {
  const [showCustomPrompt, setShowCustomPrompt] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [results, setResults] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Attachment state
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocument[]>([]);
  const [parsingDocument, setParsingDocument] = useState(false);
  
  const imageInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    for (const file of Array.from(files)) {
      if (uploadedImages.length >= MAX_IMAGES) {
        toast.error(`Maximum ${MAX_IMAGES} images allowed`);
        break;
      }

      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        toast.error(`${file.name}: Unsupported image type. Use JPG, PNG, WEBP, or GIF.`);
        continue;
      }

      if (file.size > MAX_IMAGE_SIZE) {
        toast.error(`${file.name}: Image too large. Max 10MB.`);
        continue;
      }

      // Convert to base64
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        setUploadedImages(prev => [...prev, {
          name: file.name,
          base64: base64.split(',')[1], // Remove data:image/...;base64, prefix
          preview: base64,
        }]);
      };
      reader.readAsDataURL(file);
    }

    // Reset input
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    for (const file of Array.from(files)) {
      if (uploadedDocuments.length >= MAX_DOCUMENTS) {
        toast.error(`Maximum ${MAX_DOCUMENTS} documents allowed`);
        break;
      }

      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_DOCUMENT_TYPES.includes(ext)) {
        toast.error(`${file.name}: Unsupported document type. Use PDF, DOCX, or TXT.`);
        continue;
      }

      if (file.size > MAX_DOCUMENT_SIZE) {
        toast.error(`${file.name}: Document too large. Max 25MB.`);
        continue;
      }

      setParsingDocument(true);

      try {
        // For TXT files, read directly
        if (ext === '.txt') {
          const text = await file.text();
          setUploadedDocuments(prev => [...prev, {
            name: file.name,
            text,
            wordCount: text.split(/\s+/).filter(Boolean).length,
          }]);
        } else {
          // For PDF/DOCX, we need to upload and parse
          const { supabase } = await import('@/integrations/supabase/client');
          const fileName = `ai-temp/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
          
          const { error: uploadError } = await supabase.storage
            .from('knowledge-sources')
            .upload(fileName, file);

          if (uploadError) {
            throw new Error(`Upload failed: ${uploadError.message}`);
          }

          // Get public URL
          const { data: urlData } = supabase.storage
            .from('knowledge-sources')
            .getPublicUrl(fileName);

          // Parse the document with better feedback
          toast.info(`Extracting text from ${file.name}...`);
          const parseResult = await parseDocument(urlData.publicUrl, file.name);

          // Clean up temporary file regardless of parse result
          await supabase.storage.from('knowledge-sources').remove([fileName]);

          // Handle parse result - allow partial success
          if (parseResult.extracted_text && parseResult.word_count > 0) {
            setUploadedDocuments(prev => [...prev, {
              name: file.name,
              text: parseResult.extracted_text,
              wordCount: parseResult.word_count,
            }]);
            
            if (parseResult.warning) {
              toast.warning(`${file.name}: ${parseResult.warning}`);
            }
          } else {
            // Text extraction failed but file was processed
            toast.warning(`${file.name}: Could not extract text. The file was uploaded but content may not be readable by the AI.`);
            
            // Still add the document with a placeholder so user knows it's there
            setUploadedDocuments(prev => [...prev, {
              name: file.name,
              text: `[Document: ${file.name} - text extraction failed]`,
              wordCount: 0,
            }]);
          }
        }
      } catch (err: any) {
        console.error('Document parse error:', err);
        toast.error(`Failed to process ${file.name}: ${err.message}`);
      } finally {
        setParsingDocument(false);
      }
    }

    // Reset input
    if (documentInputRef.current) {
      documentInputRef.current.value = '';
    }
  };

  const removeImage = (index: number) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeDocument = (index: number) => {
    setUploadedDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const attachments = {
        images: uploadedImages.length > 0 ? uploadedImages.map(img => ({ name: img.name, base64: img.base64 })) : undefined,
        documents: uploadedDocuments.length > 0 ? uploadedDocuments.map(doc => ({ name: doc.name, text: doc.text })) : undefined,
      };
      
      const generated = await onGenerate(customPrompt || undefined, attachments);
      
      // Deduplicate results - remove exact duplicates and near-duplicates (case-insensitive trim)
      const seen = new Set<string>();
      const uniqueResults = generated.filter(result => {
        const normalized = result.trim().toLowerCase();
        if (seen.has(normalized)) return false;
        seen.add(normalized);
        return true;
      });
      
      setResults(uniqueResults);
      setSelectedIndex(0);
    } catch (err: any) {
      setError(err.message || 'Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  const handleApply = () => {
    if (results.length > 0) {
      onApply(results[selectedIndex]);
      onOpenChange(false);
      // Reset state
      setResults([]);
      setCustomPrompt('');
      setShowCustomPrompt(false);
      setUploadedImages([]);
      setUploadedDocuments([]);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset state
    setResults([]);
    setCustomPrompt('');
    setShowCustomPrompt(false);
    setError(null);
    setUploadedImages([]);
    setUploadedDocuments([]);
  };

  // Filter out empty context values and hidden context keys
  const visibleContext = hideContextBadges 
    ? [] 
    : context.filter(c => c.value && c.value.trim() !== '' && !HIDDEN_CONTEXT_KEYS.includes(c.key));

  const hasAttachments = uploadedImages.length > 0 || uploadedDocuments.length > 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-500" />
            Generate {title}
          </DialogTitle>
          <DialogDescription className="sr-only">Use AI to generate content for {title}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Generate Button - Primary action at top */}
          <Button
            onClick={handleGenerate}
            disabled={generating || isLoading || parsingDocument}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            {generating ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate {title}
              </>
            )}
          </Button>

          {/* Custom Prompt Toggle - Only show if not hidden */}
          {!hideCustomize && (
            <>
              <button
                type="button"
                onClick={() => setShowCustomPrompt(!showCustomPrompt)}
                className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-700 transition-colors"
              >
                {showCustomPrompt ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                Customize prompt
              </button>

              {/* Custom Prompt Input and Attachments */}
              {showCustomPrompt && (
                <div className="space-y-4">
                  <Textarea
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    placeholder="Enter custom instructions for the AI..."
                    className="min-h-[80px] text-sm"
                  />

                  {/* Attachment Upload Section */}
                  {allowAttachments && (
                    <div className="space-y-3">
                      <p className="text-xs font-medium text-muted-foreground">Reference Materials (optional)</p>
                      
                      <div className="flex gap-2">
                        {/* Image Upload */}
                        <div 
                          onClick={() => imageInputRef.current?.click()}
                          className={cn(
                            "flex-1 flex flex-col items-center justify-center gap-1 p-3 rounded-lg border-2 border-dashed cursor-pointer transition-colors",
                            uploadedImages.length >= MAX_IMAGES 
                              ? "border-muted bg-muted/50 cursor-not-allowed" 
                              : "border-muted-foreground/25 hover:border-purple-500 hover:bg-purple-50"
                          )}
                        >
                          <Image className="h-5 w-5 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">Images</span>
                          <span className="text-[10px] text-muted-foreground">
                            {uploadedImages.length}/{MAX_IMAGES}
                          </span>
                        </div>
                        <input
                          ref={imageInputRef}
                          type="file"
                          accept={ALLOWED_IMAGE_TYPES.join(',')}
                          multiple
                          onChange={handleImageUpload}
                          className="hidden"
                          disabled={uploadedImages.length >= MAX_IMAGES}
                        />

                        {/* Document Upload */}
                        <div 
                          onClick={() => !parsingDocument && documentInputRef.current?.click()}
                          className={cn(
                            "flex-1 flex flex-col items-center justify-center gap-1 p-3 rounded-lg border-2 border-dashed cursor-pointer transition-colors",
                            uploadedDocuments.length >= MAX_DOCUMENTS || parsingDocument
                              ? "border-muted bg-muted/50 cursor-not-allowed" 
                              : "border-muted-foreground/25 hover:border-purple-500 hover:bg-purple-50"
                          )}
                        >
                          {parsingDocument ? (
                            <Loader2 className="h-5 w-5 text-purple-500 animate-spin" />
                          ) : (
                            <FileText className="h-5 w-5 text-muted-foreground" />
                          )}
                          <span className="text-xs text-muted-foreground">Documents</span>
                          <span className="text-[10px] text-muted-foreground">
                            {uploadedDocuments.length}/{MAX_DOCUMENTS}
                          </span>
                        </div>
                        <input
                          ref={documentInputRef}
                          type="file"
                          accept={ALLOWED_DOCUMENT_TYPES.join(',')}
                          multiple
                          onChange={handleDocumentUpload}
                          className="hidden"
                          disabled={uploadedDocuments.length >= MAX_DOCUMENTS || parsingDocument}
                        />
                      </div>

                      {/* Uploaded Images Preview */}
                      {uploadedImages.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {uploadedImages.map((img, index) => (
                            <div key={index} className="relative group">
                              <img 
                                src={img.preview} 
                                alt={img.name}
                                className="h-16 w-16 object-cover rounded-lg border"
                              />
                              <button
                                type="button"
                                onClick={() => removeImage(index)}
                                className="absolute -top-1 -right-1 p-0.5 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="h-3 w-3" />
                              </button>
                              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[8px] p-0.5 rounded-b-lg truncate">
                                {img.name}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Uploaded Documents Preview */}
                      {uploadedDocuments.length > 0 && (
                        <div className="space-y-1">
                          {uploadedDocuments.map((doc, index) => (
                            <div 
                              key={index} 
                              className={cn(
                                "flex items-center justify-between gap-2 p-2 rounded-lg text-sm",
                                doc.wordCount === 0 ? "bg-amber-50 border border-amber-200" : "bg-muted"
                              )}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText className={cn(
                                  "h-4 w-4 flex-shrink-0",
                                  doc.wordCount === 0 ? "text-amber-500" : "text-muted-foreground"
                                )} />
                                <span className="truncate">{doc.name}</span>
                                {doc.wordCount === 0 ? (
                                  <span className="text-xs text-amber-600 font-medium">
                                    (extraction failed)
                                  </span>
                                ) : (
                                  <span className="text-xs text-muted-foreground">
                                    ({doc.wordCount.toLocaleString()} words)
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => removeDocument(index)}
                                className="p-1 hover:bg-destructive/20 rounded transition-colors"
                              >
                                <X className="h-3 w-3 text-destructive" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Error Display */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Results */}
          {results.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">
                Select an option {results.length > 1 && `(${selectedIndex + 1} of ${results.length})`}
              </p>

              {/* Result Cards */}
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {results.map((result, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedIndex(index)}
                    className={cn(
                      'p-3 rounded-lg border cursor-pointer transition-all',
                      selectedIndex === index
                        ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    )}
                  >
                    <div className="flex items-start gap-2">
                      {selectedIndex === index && (
                        <Check className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
                      )}
                      <p className={cn(
                        'text-sm',
                        multiline ? 'whitespace-pre-wrap' : '',
                        selectedIndex === index ? 'text-purple-900' : 'text-gray-700'
                      )}>
                        {result}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Regenerate Button */}
              {results.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGenerate}
                  disabled={generating}
                  className="w-full"
                >
                  <RefreshCw className={cn('h-4 w-4 mr-2', generating && 'animate-spin')} />
                  Generate More Options
                </Button>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              disabled={results.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Check className="h-4 w-4 mr-2" />
              Apply
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Compact inline button for triggering AI generation
export interface AIGenerateButtonProps {
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  tooltip?: string;
}

export function AIGenerateButton({ onClick, disabled, className, tooltip = 'Generate with AI' }: AIGenerateButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={tooltip}
      className={cn(
        'p-1.5 rounded-md text-purple-500 hover:text-purple-700 hover:bg-purple-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
        className
      )}
    >
      <Sparkles className="h-4 w-4" />
    </button>
  );
}

// Batch generation modal for generating multiple fields at once
export interface BatchGenerateField {
  field: string;
  label: string;
  enabled: boolean;
}

export interface AIBatchGenerateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentType: string;
  fields: BatchGenerateField[];
  onFieldToggle: (field: string) => void;
  onGenerate: () => Promise<Record<string, string>>;
  onApply: (values: Record<string, string>) => void;
  isLoading?: boolean;
}

export function AIBatchGenerateModal({
  open,
  onOpenChange,
  agentType,
  fields,
  onFieldToggle,
  onGenerate,
  onApply,
  isLoading = false,
}: AIBatchGenerateModalProps) {
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const enabledFields = fields.filter(f => f.enabled);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const generated = await onGenerate();
      setResults(generated);
    } catch (err: any) {
      setError(err.message || 'Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  const handleApply = () => {
    onApply(results);
    onOpenChange(false);
    setResults({});
  };

  const handleClose = () => {
    onOpenChange(false);
    setResults({});
    setError(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-purple-500" />
            Batch Generate Content
          </DialogTitle>
          <DialogDescription className="sr-only">Generate multiple content fields at once using AI</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Generate multiple fields at once for your {agentType} agent
          </p>

          {/* Field Selection */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Select fields to generate:</p>
            <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
              {fields.map((field) => (
                <label
                  key={field.field}
                  className={cn(
                    'flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors',
                    field.enabled
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={field.enabled}
                    onChange={() => onFieldToggle(field.field)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm">{field.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={generating || enabledFields.length === 0}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            {generating ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Generating {enabledFields.length} fields...
              </>
            ) : (
              <>
                <Wand2 className="h-4 w-4 mr-2" />
                Generate {enabledFields.length} Fields
              </>
            )}
          </Button>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Results Preview */}
          {Object.keys(results).length > 0 && (
            <div className="space-y-2 max-h-[250px] overflow-y-auto">
              <p className="text-sm font-medium text-gray-700">Generated Content:</p>
              {Object.entries(results).map(([field, value]) => {
                const fieldInfo = fields.find(f => f.field === field);
                return (
                  <div key={field} className="p-3 bg-gray-50 rounded-lg border">
                    <p className="text-xs font-medium text-gray-500 mb-1">
                      {fieldInfo?.label || field}
                    </p>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">
                      {value.length > 200 ? value.substring(0, 200) + '...' : value}
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              disabled={Object.keys(results).length === 0}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Check className="h-4 w-4 mr-2" />
              Apply All
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
